import { Router } from '@angular/router';
import { NotificationActionRequest } from '@smatchy/notification';

/**
 * The screens a notification can only open for one particular record.
 *
 * Found on 16 October 2026 (Asana 1219582084100961, by the Emails vertical):
 * the guard below used to refuse every notification without an id, before
 * looking at its type. The activation journey and the quiet-area nudge send
 * `{ type: 'home', id: '' }`, because the screen they open belongs to nobody,
 * so a tap woke the application and left it on whatever screen it was on.
 * An id is required where the route is built from it, and nowhere else.
 */
const NEEDS_AN_ID: ReadonlySet<NotificationActionRequest['type']> = new Set([
  'chat',
  'event',
  'paid_event',
  'group',
  'member',
  'insurance-renewal-required',
  'request',
  'after-outing',
]);

export function navigateFromNotificationAction(
  router: Router,
  action: NotificationActionRequest,
  options?: { category?: string },
): void {
  if (action?.action !== 'routing_event') {
    return;
  }
  if (NEEDS_AN_ID.has(action.type) && !action.id) {
    return;
  }

  switch (action.type) {
    case 'chat':
      void router.navigate(['/home/message/discussion', action.id], {
        state: { fromNotification: true },
      });
      break;
    case 'event':
    case 'paid_event':
      void router.navigate(['/home/detail-activity', action.id]);
      break;
    case 'group':
      void router.navigate(['/home/group', action.id]);
      break;
    case 'home':
      if (options?.category === 'accounts') {
        void router.navigate(['/home/profile/stripe-connect-onboarding']);
        break;
      }
      void router.navigate(['/home/explore']);
      break;
    case 'profile-completion':
      // The list of criteria is a panel on the profile screen, not a route.
      // The screen opens it from this parameter.
      void router.navigate(['/home/profile/edit'], { queryParams: { sheet: 'completion' } });
      break;
    case 'member':
      void router.navigate(['/home/member', action.id]);
      break;
    case 'insurance-renewal-required':
      void router.navigate(['/home/profile'], {
        queryParams: { step: 'renew-insurance', requestId: action.id },
      });
      break;
    case 'request':
      void router.navigate(['/home/profile/earn-money'], {
        queryParams: { step: 'application-status-detail', requestId: action.id },
      });
      break;
    case 'premium-permission':
      void router.navigate(['/home/profile'], {
        queryParams: { step: 'premium-permission' },
      });
      break;
    case 'streak':
      void router.navigate(['/home/profile/my-streak']);
      break;
    case 'duo':
      void router.navigate(['/home/profile/my-duo']);
      break;
    case 'organiser-rank':
      // « Rang atteint » (20 October 2026): the road of « Mes accomplissements »,
      // where the rank lives, as the tier push opens Ma série. The screen of the
      // rank opens by itself at that opening if it was not shown yet.
      void router.navigate(['/home/profile/my-achievements']);
      break;
    case 'league':
      void router.navigate(['/home/profile/my-league']);
      break;
    case 'badge':
      // The page of the badge told about, not the list: a notice that says
      // « Nouveau badge : Habitué » and opens on 28 badges makes the person
      // look for it. Its back button leads to the list. Changed on 14 October
      // 2026; the API has sent the badge id since the first notice.
      void router.navigate(['/home/profile/my-badges/badge', action.id]);
      break;
    case 'stripe-connect':
      void router.navigate(['/home/profile/stripe-connect-onboarding']);
      break;
    case 'vat-status':
      // The VAT status ask: the wallet, whose banner opens the status screen.
      void router.navigate(['/home/profile/my-wallet']);
      break;
    case 'follow-requests':
      // Sent by somebody-asked-to-follow-you.handler.ts since the private
      // profile shipped, and opened nothing until 16 October 2026: the type had
      // no case here. The id is the private profile's own, and the screen is
      // the list of people waiting, so it is not needed.
      void router.navigate(['/home/profile/follow-requests']);
      break;
    case 'friends':
      // A friend request or a friendship (22 October 2026): « Mes amis »,
      // where a request is answered. The member's own page used to open, with
      // no way to answer from it.
      void router.navigate(['/home/profile/my-friends']);
      break;
    case 'referral':
      // A referral rewarded (22 October 2026). « Mes amis » holds the block
      // « Invite quelqu'un » until the screen « Mes parrainages » exists.
      void router.navigate(['/home/profile/my-friends']);
      break;
    case 'after-outing':
      // The morning after an outing (17 October 2026): who else was in it, and
      // « Préviens-moi de la prochaine ». The id is the activity's.
      void router.navigate(['/home/apres-sortie', action.id]);
      break;
    default:
      // A type this build does not know, which is what an older application
      // meets when the API learns a new one. Opening the home screen is better
      // than a tap that wakes the application and does nothing: that silence
      // is how follow-requests went unnoticed.
      void router.navigate(['/home/explore']);
      break;
  }
}
